<template>
  <div>
    <v-card-title>
      <v-spacer />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter"
      />
    </v-card-title>
    <v-tooltip
      top
      dark
    >
      <template v-slot:activator="{ on }">
        <v-btn
          color="secondary"
          fab
          dark
          absolute
          top
          right
          @click.stop="onCreate"
          v-on="on"
        >
          <v-icon>note_add</v-icon>
        </v-btn>
      </template>
      <span>Create new Payout</span>
    </v-tooltip>
    <v-tooltip
      top
      dark
    >
      <template v-slot:activator="{ on }">
        <v-btn
          color="secondary"
          fab
          dark
          absolute
          top
          right
          style="right:80px;"
          @click.stop="csvUploadingDialog=true"
          v-on="on"
        >
          <v-icon>publish</v-icon>
        </v-btn>
      </template>
      <span>Upload Payouts</span>
    </v-tooltip>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
      :rows-per-page-items="[25,50,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr
          @click="toggleSelected(props.item)"
        >
          <td>
            {{ props.item.description }}
          </td>
          <td>
            {{ parseFloat(getPayoutAmount(props.item)) || '-' |toCurrency }}
          </td>
          <td>
            {{ props.item.security_id }}
          </td>
          <td>
            {{ types()[props.item.type] }}
          </td>
          <td>
            <DateDisplay
              :date="props.item.interest_period_begin"
            />
          </td>
          <td>
            <DateDisplay
              :date="props.item.interest_period_end"
            />
          </td>
          <td>
            <DateDisplay
              :date="props.item.expected_payout_date"
            />
          </td>
          <td>
            <DateDisplay
              :date="props.item.created_at"
            />
          </td>
          <td class="text-xs">
            <v-chip
              v-if="props.item.errors"
              class="ma-2"
              color="red"
              text-color="white"
              @click.stop="toggleErrors(props.item.errors)"
            >
              Errors!
            </v-chip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="!isAutomaticCashbackPayout(props.item)"
                  dark
                  flat
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="() => showDialog(props.item)"
                >
                  <v-icon>check</v-icon>
                </v-btn>
              </template>
              <span>Approve</span>
            </v-tooltip>

            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="!isAutomaticCashbackPayout(props.item)"
                  dark
                  flat
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="() => onEdit(props.item)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="error"
                  v-on="on"
                  @click.stop="() => showDeleteDialog(props.item)"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              <span>Delete Payout</span>
            </v-tooltip>
            <v-tooltip
              v-if="props.item.security && props.item.security.type === 'loan'"
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="warning"
                  v-on="on"
                  @click.stop="() => showStartInterestCalculationDialog(props.item)"
                >
                  <v-icon>sync</v-icon>
                </v-btn>
                <v-btn
                  v-if="props.item.security && props.item.security.type === 'loan'"
                  dark
                  flat
                  icon
                  color="primary"
                  @click.stop="() => downloadCsv(props.item.payout_id)"
                >
                  <v-icon>download</v-icon>
                </v-btn>
              </template>
              <span>Start Interest Calculation</span>
            </v-tooltip>
          </td>
        </tr>
        <tr v-if="displayErrors === props.item.errors">
          <td colspan="12">
            <ErrorDetails
              :errors="props.item.errors"
            />
          </td>
        </tr>
        <tr v-if="props.item.payout_id && selected === props.item.payout_id">
          <td colspan="11">
            <PayoutDetails
              :payout="props.item"
              :holders="props.item.holders"
              :show-holders="true"
              :show-holders-payback-information="true"
              :allow-to-delete-holders="true"
            />
          </td>
        </tr>
      </template>
    </v-data-table>

    <CreateDialog
      v-if="isCreating"
      :is-open="isCreating"
      :payout="currentPayout"
      :toggle-open="onCreate"
    />

    <PayoutUploadCSVDialog
      :visible="csvUploadingDialog"
      @close="csvUploadingDialog=false"
      @fileUpload="(file) => onUploadCsv(file)"
    />

    <ApprovalsApproveDialog
      key="approve_dialog"
      :visible="isDialogActive()"
      :payout="currentPayout"
      @close="hideDialog()"
      @submit="onApprove()"
    />

    <DeleteDialog
      key="delete_dialog"
      :visible="isDeleteDialogActive()"
      :payout="currentPayout"
      @close="hideDeleteDialog()"
      @submit="onDelete()"
    />

    <StartInterestCalculationDialog
      key="start_interest_calculation_dialog"
      :visible="isStartInterestCalculationDialogActive()"
      :payout="currentPayout"
      @close="hideStartInterestCalculationDialog()"
      @submit="onStartInterestCalculation()"
    />

    <v-dialog
      v-model="csvResultDialog"
      width="800"
    >
      <v-card
        color="primary"
        dark
        class="pa-3"
      >
        <p class="font-weight-black">
          Uploading CSV finished - Result:
        </p>
        <p>Total in CSV: {{ csvUploadingResult.totalInCsv }}</p>
        <p>Total Valid: {{ csvUploadingResult.totalImported }}</p>
        <p>Total Saved to DB: {{ csvUploadingResult.totalDBInserts }}</p>
        <p>Total Failed: {{ csvUploadingResult.totalFailed }}</p>
        <p
          v-for="item in csvUploadingResult.failedReasons"
          :key="item.index"
          style="background-color: red;"
          class="pa-1"
        >
          {{ item.message }}
        </p>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbarShow"
      :color="snackbarColor"
      :auto-height="true"
    >
      <ul v-if="snackbarErrors">
        <li
          v-for="snackbarError in snackbarErrors"
          :key="snackbarError.message"
        >
          {{ snackbarError.message }}
        </li>
      </ul>

      <div
        v-if="snackbarMessage"
      >
        {{ snackbarMessage }}
      </div>

      <v-btn
        color="primary"
        text
        @click="snackbarShow = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import ApprovalsApproveDialog from './dialogs/payout/ApprovalsApproveDialog';
import CreateDialog from './dialogs/payout/CreateDialog';
import PayoutDetails from '@/components/payoutsAndTaxes/payout/shared/PayoutDetails';
import ErrorDetails from '@/components/payoutsAndTaxes/payout/shared/ErrorDetails';
import DeleteDialog from '@/components/payoutsAndTaxes/payout/dialogs/payout/DeleteDialog';
import StartInterestCalculationDialog from '@/components/payoutsAndTaxes/payout/dialogs/payout/StartInterestCalculationDialog';
import DateDisplay from '@/components/payoutsAndTaxes/payout/shared/DateDisplay';
import { downloadCsvFile } from '@/helpers';
import { downloadFromS3 } from '@/helpers/axiosS3Downloader';
import PayoutUploadCSVDialog from '@/components/payoutsAndTaxes/payout/dialogs/payout/PayoutUploadCSVDialog';

export default {
  name: 'PayoutApprovals',
  components: {
    PayoutUploadCSVDialog,
    PayoutDetails,
    ErrorDetails,
    ApprovalsApproveDialog,
    CreateDialog,
    DeleteDialog,
    StartInterestCalculationDialog,
    DateDisplay,
  },
  data() {
    return {
      snackbarShow: false,
      snackbarMessage: '',
      snackbarColor: 'error',
      snackbarErrors: [],
      csvUploadingDialog: false,
      csvUploadingResult: {
        totalDBInserts: 0,
        totalFailed: 0,
        totalImported: 0,
        totalInCsv: 0,
        failedReasons: [],
      },
      csvResultDialog: false,
      selected: 0,
      currentPayout: {},
      displayErrors: {},
      showContractId: null,
      isLoading: false,
      isCreating: false,
      isShowing: false,
      isEditing: false,
      approvalDialog: false,
      deleteDialog: false,
      startInterestCalculationDialog: false,
      pagination: {
        rowsPerPage: 25,
      },
      search: '',
      headers: [
        { text: 'Payout', value: 'description' },
        { text: 'Amount', value: 'amount' },
        { text: 'Security ID', value: 'security_id' },
        { text: 'Type', value: 'type' },
        { text: 'Interest Period Begin', value: 'interest_period_begin' },
        { text: 'Interest Period End', value: 'interest_period_end' },
        { text: 'Expected Payout Date', value: 'expected_payout_date' },
        { text: 'Created At', value: 'created_at' },
        { text: 'Actions', value: 'actions' },
      ],
    };
  },
  computed: {
    payoutPendingApprovals() {
      return this.$store.getters.getPayoutsApprovalPending;
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.payoutPendingApprovals :
        this.payoutPendingApprovals.filter((item) => {
          let found = false;
          Object.keys(item)
            .map((key) => {
              if (item[key] && String(item[key]).toLowerCase().indexOf(search.toLowerCase()) > -1) {
                found = true;
              }

              return key;
            });

          return found;
        });
    },
  },
  methods: {
    getPayoutAmount(payout) {
      if (!payout.holders || payout.holders.length === 0) {
        return payout.amount;
      }

      const initialSumValue = 0;

      return payout.holders.reduce(
        (sum, holder) => sum + holder.payback_information.gross_amount.total || 0,
        initialSumValue,
      );
    },
    types() {
      const data = this.$store.getters.getPayoutsCreationTypes;
      if (!data || !data.payout || !data.payout.types) {
        return {};
      }

      return data.payout.types;
    },
    toggleErrors(errors) {
      this.displayErrors = this.displayErrors === errors ? null : errors;
    },
    onCreate() {
      this.isCreating = !this.isCreating;
      this.currentPayout = {};
    },
    onEdit(item) {
      this.isCreating = !this.isCreating;
      this.currentPayout = item;
    },
    onApprove() {
      this.$store.dispatch('callPayoutApprovalApprove', this.currentPayout.payout_id)
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Payout successfully approved';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    onDelete() {
      this.$store.dispatch('callPayoutDelete', this.currentPayout.payout_id)
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Payout Deleted!';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    onStartInterestCalculation() {
      this.$store.dispatch('callPayoutStartInterestCalculation', this.currentPayout.payout_id)
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Interest Calculation for payout started!';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    isDeleteDialogActive() {
      return this.deleteDialog === true;
    },
    showDeleteDialog(item) {
      this.currentPayout = item;
      this.deleteDialog = true;
      this.$forceUpdate();
    },
    hideDeleteDialog() {
      this.deleteDialog = false;
      this.$forceUpdate();
    },
    isDialogActive() {
      return this.approvalDialog === true;
    },
    showDialog(item) {
      this.currentPayout = item;
      this.approvalDialog = true;
      this.$forceUpdate();
    },
    hideDialog() {
      this.approvalDialog = false;
      this.$forceUpdate();
    },
    isAutomaticCashbackPayout(payout) {
      return payout.type === 'cashback_v2';
    },
    isStartInterestCalculationDialogActive() {
      return this.startInterestCalculationDialog === true;
    },
    showStartInterestCalculationDialog(item) {
      this.currentPayout = item;
      this.startInterestCalculationDialog = true;
      this.$forceUpdate();
    },
    hideStartInterestCalculationDialog() {
      this.startInterestCalculationDialog = false;
      this.$forceUpdate();
    },
    toggleSelected(payout) {
      this.selected = this.selected === payout.payout_id ? null : payout.payout_id;
      this.loadPayoutHolders(payout);
    },
    loadPayoutHolders(payout) {
      if (payout.holders && payout.holders.length !== 0) {
        return;
      }

      this.$store.dispatch('fetchPayoutDataGetHolders', {
        origin: 'approval',
        payoutId: payout.payout_id,
      })
        .then(() => {
          this.$forceUpdate();
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    downloadCsv(payoutId) {
      this.$store
        .dispatch('fetchPayoutApprovalDownloadCsv', payoutId)
        .then((response) => {
          downloadFromS3(response.data).then((data) => {
            downloadCsvFile(data, response.data.fileName);
          });
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    onUploadCsv(file) {
      this.csvUploadingDialog = false;
      this.$store
        .dispatch('callPayoutImportCSV', { file })
        .then((e) => {
          this.csvUploadingDialog = false;
          this.csvUploadingResult = e;
          this.csvResultDialog = true;
          this.$store.dispatch('fetchPayoutAll');
        })
        .catch((error) => {
          this.csvUploadingDialog = false;
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
  },
};
</script>
<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/PayoutApprovals.vue